import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { Play, Stop, Truck, Lock, Camera, X, ArrowLeft } from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_BASE}/api` });

function formatDuur(min) { if (!min) return "0u 0m"; const h = Math.floor(min/60); const m = Math.round(min%60); return `${h}u ${m}m`; }

function PhotoUploader({ photos, onPhotosChange, label, maxPhotos = 5 }) {
  const fileRef = useRef(null);
  const addPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const toAdd = files.slice(0, maxPhotos - photos.length);
    onPhotosChange([...photos, ...toAdd.map(f => ({ file: f, preview: URL.createObjectURL(f) }))]);
    e.target.value = "";
  };
  const removePhoto = (idx) => onPhotosChange(photos.filter((_, i) => i !== idx));

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Camera size={16} className="text-steel-600" />
        <span className="text-sm font-semibold text-steel-800">{label}</span>
        <span className="text-[10px] text-steel-400">({photos.length}/{maxPhotos})</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {photos.map((p, i) => (
          <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden border border-steel-200">
            <img src={p.preview || `${API_BASE}${p.url}`} alt="" className="w-full h-full object-cover" />
            <button onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"><X size={10} weight="bold" /></button>
          </div>
        ))}
        {photos.length < maxPhotos && (
          <button onClick={() => fileRef.current?.click()} className="w-20 h-20 border-2 border-dashed border-steel-300 rounded-sm flex flex-col items-center justify-center text-steel-400 hover:border-steel-500 hover:text-steel-600 transition-colors">
            <Camera size={20} /><span className="text-[9px] mt-0.5">Foto</span>
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={addPhoto} />
    </div>
  );
}

export default function RitPage() {
  const [step, setStep] = useState("pin");
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [driver, setDriver] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [activeRit, setActiveRit] = useState(null);
  const [selVehicle, setSelVehicle] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("voertuig") || ""; }
    catch { return ""; }
  });
  const qrFirma = (() => {
    try { return new URLSearchParams(window.location.search).get("firma") || ""; }
    catch { return ""; }
  })();
  const [msg, setMsg] = useState("");
  const [startPhotos, setStartPhotos] = useState([]);
  const [endPhotos, setEndPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadDriverData = useCallback(async (did) => {
    try {
      const { data: v } = await api.get(`/driver-public/${did}/vehicles`);
      setVehicles(v);
      const { data: a } = await api.get(`/driver-public/${did}/active`);
      if (a && a.rid) setActiveRit(a); else setActiveRit(null);
    } catch {}
  }, []);

  const [keuzes, setKeuzes] = useState([]);

  const kiesBestuurder = async (d) => {
    setKeuzes([]);
    setDriver(d);
    await loadDriverData(d.did);
    setStep("dashboard");
  };

  const verifyPin = async () => {
    if (pin.length < 4) return;
    setPinError("");
    try {
      const { data } = await api.post("/driver-public/pin-login", {
        pin,
        firma_id: qrFirma,
        vehicle_vid: selVehicle,
      });
      if (data.meerdere) { setKeuzes(data.meerdere); return; }
      await kiesBestuurder(data);
    } catch (e) {
      setPinError(e.response?.data?.detail || "Ongeldige PIN");
    }
  };

  const uploadPhotos = async (did, phase, photos) => {
    if (photos.length === 0) return;
    const fd = new FormData();
    photos.forEach(p => { if (p.file) fd.append("files", p.file); });
    if ([...fd.entries()].length === 0) return;
    try {
      await api.post(`/driver-public/${did}/trip-photos?phase=${phase}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch {}
  };

  const startRit = async () => {
    if (!selVehicle || !driver) return;
    setUploading(true);
    try {
      await api.post(`/driver-public/${driver.did}/start`, { vehicle_vid: selVehicle });
      if (startPhotos.length > 0) await uploadPhotos(driver.did, "start", startPhotos);
      setMsg("Rit gestart!");
      setStartPhotos([]);
      await loadDriverData(driver.did);
    } catch (e) { setMsg(e.response?.data?.detail || "Fout"); }
    setUploading(false);
    setTimeout(() => setMsg(""), 3000);
  };

  const stopRit = async () => {
    if (!driver) return;
    setUploading(true);
    try {
      if (endPhotos.length > 0) await uploadPhotos(driver.did, "eind", endPhotos);
      const { data } = await api.post(`/driver-public/${driver.did}/stop`);
      setMsg(`Rit gestopt - ${formatDuur(data.duur_minuten)}`);
      setActiveRit(null);
      setEndPhotos([]);
      await loadDriverData(driver.did);
    } catch (e) { setMsg(e.response?.data?.detail || "Fout"); }
    setUploading(false);
    setTimeout(() => setMsg(""), 5000);
  };

  const logout = () => {
    setStep("pin"); setPin(""); setDriver(null); setActiveRit(null);
    setVehicles([]); setSelVehicle(""); setStartPhotos([]); setEndPhotos([]);
  };

  if (step === "pin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-steel-100 p-4">
        <div className="bg-white border border-steel-200 rounded-sm p-8 w-full max-w-xs text-center">
          <div className="font-heading text-base font-black tracking-tighter text-steel-900 mb-0.5">Rit registratie</div>
          <div className="text-[9px] text-steel-400 tracking-wide mb-6">Voertuig in- en uitchecken</div>
          {keuzes.length > 0 ? (
            <div data-testid="firma-keuze">
              <p className="text-sm font-semibold text-steel-700 mb-3">Bij welke firma werk je nu?</p>
              <div className="flex flex-col gap-2">
                {keuzes.map((k) => (
                  <button
                    key={k.did}
                    onClick={() => kiesBestuurder(k)}
                    className="w-full py-3 px-3 text-sm font-bold bg-steel-900 text-white rounded-sm hover:bg-steel-800 transition-colors"
                    data-testid={`firma-keuze-${k.did}`}
                  >
                    {k.firma_naam || "Onbekende firma"}
                    <span className="block text-[10px] font-normal text-steel-400">{k.naam}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
          <Lock size={32} className="mx-auto text-steel-700 mb-3" />
          <p className="text-sm font-semibold text-steel-700 mb-1">Voer uw PIN in</p>
          <p className="text-xs text-steel-400 mb-4">Uw PIN code identificeert u als bestuurder</p>
          {pinError && <div data-testid="pin-error" className="text-loss text-sm mb-3 font-semibold">{pinError}</div>}
          <input
            data-testid="universal-pin-input"
            type="password" inputMode="numeric" maxLength={6} value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
            onKeyDown={e => e.key === "Enter" && verifyPin()}
            className="w-full px-4 py-4 border border-steel-200 text-center text-3xl font-mono font-bold tracking-[0.5em] rounded-sm focus:outline-none focus:ring-2 focus:ring-steel-900 mb-4"
            placeholder="****" autoFocus
          />
          <button data-testid="universal-pin-submit" onClick={verifyPin} disabled={pin.length < 4}
            className="w-full py-3 text-sm font-bold bg-steel-900 text-white rounded-sm hover:bg-steel-800 disabled:opacity-40 transition-colors">
            Bevestigen
          </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-steel-100 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-steel-900 text-white rounded-sm p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-xl font-bold">{driver?.naam}</h1>
              <p className="text-steel-400 text-xs">{driver?.firma_naam || ""} - Bestuurder</p>
            </div>
            <button data-testid="rit-logout" onClick={logout}
              className="flex items-center gap-1 text-steel-400 hover:text-white text-xs transition-colors">
              <ArrowLeft size={14} /> Uitloggen
            </button>
          </div>
        </div>

        {msg && <div className="bg-green-50 border border-green-200 text-profit text-sm font-semibold px-4 py-3 rounded-sm mb-4 text-center">{msg}</div>}

        {activeRit ? (
          <div className="bg-white border-2 border-green-300 rounded-sm overflow-hidden mb-4">
            <div className="bg-green-600 px-4 py-3 text-center">
              <div className="text-white font-bold text-lg">{activeRit.vehicle_kenteken}</div>
              <div className="text-green-200 text-xs">{activeRit.vehicle_info}</div>
              <div className="flex items-center justify-center gap-1 mt-1">
                <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
                <span className="text-white text-xs font-bold">ACTIEF</span>
              </div>
            </div>
            <div className="p-5">
              <div className="flex justify-between mb-2 text-sm">
                <span className="text-steel-500">Gestart</span>
                <span className="font-mono font-bold">{new Date(activeRit.start_tijd).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              <div className="flex justify-between mb-4 text-sm">
                <span className="text-steel-500">Duur</span>
                <span className="font-mono text-2xl font-black text-profit">{formatDuur((new Date() - new Date(activeRit.start_tijd)) / 60000)}</span>
              </div>
              <PhotoUploader photos={endPhotos} onPhotosChange={setEndPhotos} label="Foto's einde rit (schade)" />
              <button data-testid="rit-stop-btn" onClick={stopRit} disabled={uploading}
                className="w-full flex items-center justify-center gap-2 py-4 text-lg font-bold bg-loss text-white rounded-sm hover:bg-red-700 disabled:opacity-60 transition-colors">
                {uploading ? "Bezig..." : <><Stop size={24} weight="fill" /> STOP</>}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-steel-200 rounded-sm p-5 mb-4">
            <h3 className="text-sm font-bold text-steel-900 mb-3 flex items-center gap-2">
              <Truck size={18} className="text-steel-700" /> Voertuig kiezen
            </h3>
            <div className="space-y-2 mb-4">
              {vehicles.map(v => (
                <button key={v.vid} data-testid={`rit-vehicle-${v.vid}`}
                  onClick={() => setSelVehicle(v.vid)}
                  className={`w-full text-left px-4 py-3 border rounded-sm transition-colors ${
                    selVehicle === v.vid ? "border-profit bg-green-50" : "border-steel-200 hover:bg-steel-50"
                  }`}>
                  <div className="font-bold text-sm text-steel-900">{v.kenteken}</div>
                  <div className="text-xs text-steel-500">{v.merk} {v.model}</div>
                </button>
              ))}
              {vehicles.length === 0 && <div className="text-sm text-steel-400 text-center py-4">Alle voertuigen zijn momenteel in gebruik door een andere bestuurder</div>}
            </div>
            <PhotoUploader photos={startPhotos} onPhotosChange={setStartPhotos} label="Foto's start rit (staat voertuig)" />
            <button data-testid="rit-start-btn" onClick={startRit} disabled={!selVehicle || uploading}
              className="w-full flex items-center justify-center gap-2 py-4 text-lg font-bold bg-profit text-white rounded-sm hover:bg-green-700 disabled:opacity-40 transition-colors">
              {uploading ? "Bezig..." : <><Play size={24} weight="fill" /> START RIT</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
